import type { ThemeBackgroundColors, ThemeTextColor } from '@rover/kibble/styles/themeTypes';

import { KibbleFormFieldComponentCommonProps } from '../../types';
import { SharedTypographyProps } from '../sharedTypography';

/** Styling configuration for select field states */
export type SelectFieldVariant = {
  /** Border color token */
  borderColor: string;
  /** Text color token */
  textColor: ThemeTextColor;
  /** Background color token */
  backgroundColor: ThemeBackgroundColors;
};

/** Configuration for a single select option */
export type SelectFieldOptionConfig = {
  /** Display text for the option */
  label: string;
  /** Value submitted when option is selected */
  value: string;
  /** Disable this option */
  disabled?: boolean;
};

/** Common props for SelectField across web and native */
export type SelectFieldCommonProps = KibbleFormFieldComponentCommonProps &
  SharedTypographyProps & {
    /** Array of selectable options */
    options: SelectFieldOptionConfig[];
    /** Placeholder text when no option is selected */
    placeholder: string;
    /** Change handler called when selection changes */
    onChange?: (selectedOption: SelectFieldOptionConfig['value']) => void;
    /** Width of the select input control */
    inputWidth?: string;
    /** Variant-specific configuration */
    variantOptions?: {
      /** Fixed width for dropdown menu */
      dropdownWidth?: string;
    };
    /** RxN-only: Adjust dropdown position for variable nav bar heights per platform */
    rxnNavBarHeightAdjust?: {
      /** iOS nav bar height adjustment in pixels */
      ios?: number;
      /** Android nav bar height adjustment in pixels */
      android?: number;
    };
  };

export const getVariantStyles = (
  hasError: boolean,
  isSelected: boolean,
  isDisabled?: boolean
): SelectFieldVariant => {
  if (isDisabled) {
    return {
      borderColor: 'neutral.200',
      textColor: 'disabled',
      backgroundColor: 'interactive.disabled',
    };
  }

  return {
    borderColor: hasError ? 'border.error' : 'border.primary',
    textColor: isSelected ? 'primary' : 'tertiary',
    backgroundColor: 'primary',
  };
};
